import { cn, formatDateTime } from '@/utils';

interface RecordTimestampsProps {
  createdAt?: string | null;
  updatedAt?: string | null;
  className?: string;
}

/**
 * A6: the audit footer shared by the policy, quote, contact and claim detail
 * screens — when the record was created, and when it was last touched.
 *
 * `created_at`/`updated_at` come from `core.BaseModel` and were already on all
 * four serializers; only the display was missing, which is why this is a purely
 * presentational component with no fetching of its own.
 *
 * Both stamps render even when they are identical: "created and never edited"
 * is a real answer to the question this footer exists to answer, and dropping
 * the second line would make an untouched record look like one whose
 * `updated_at` never arrived.
 */
export function RecordTimestamps({ createdAt, updatedAt, className }: RecordTimestampsProps) {
  const created = formatDateTime(createdAt);
  const updated = formatDateTime(updatedAt);

  // formatDateTime returns '' for null/undefined and for anything unparseable,
  // so this also covers an endpoint that stops sending the columns.
  if (!created && !updated) return null;

  return (
    <div
      className={cn(
        'mt-6 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-400 dark:text-gray-500',
        className
      )}
    >
      {created && (
        <span className="whitespace-nowrap">
          <span>Created</span>{' '}
          <span className="font-medium text-gray-500 dark:text-gray-400">{created}</span>
        </span>
      )}
      {updated && (
        <span className="whitespace-nowrap">
          <span>Updated</span>{' '}
          <span className="font-medium text-gray-500 dark:text-gray-400">{updated}</span>
        </span>
      )}
    </div>
  );
}
